import * as math from "mathjs";

class CartPole {
  n: number;
  theta: number;
  thetaDot: number;
  x: number;
  xDot: number;
  g: number;
  mc: number;
  mp: number;
  l: number;
  kpTheta: number;
  kiTheta: number;
  kdTheta: number;
  kpX: number;
  kiX: number;
  kdX: number;
  mtocenti: number;
  fx: number;
  xOffset: number;
  xIntegration: number[];

  constructor(
    n = 1,
    theta = 0.1 * Math.PI,
    thetaDot = 0,
    x = 0,
    xDot = 0,
    g = -9.8,
    mc = 1,
    mp = 2,
    l = 1.5,
    kp = 5,
    kd = 1,
    thetaOffset = 0
  ) {
    this.n = n;
    this.theta = theta;
    this.thetaDot = thetaDot;
    this.x = x;
    this.xDot = xDot;
    this.g = g;
    this.mc = mc;
    this.mp = mp;
    this.l = l;
    this.kpTheta = kp;
    this.kdTheta = kd;
    this.mtocenti = 100;
    this.fx = this.kpTheta * this.theta + this.kdTheta * this.thetaDot;
    this.xOffset = thetaOffset;
    this.xIntegration = [];
  }

  restart() {
    this.theta = 0.1 * Math.PI;
    this.thetaDot = 0;
    this.x = 0;
    this.xDot = 0;
  }

  f(theta, thetaDot, x, xDot) {
    this.fx =
      this.kpTheta * theta +
      this.kdTheta * thetaDot +
      this.kpX * x +
      this.kdX * xDot +
      this.kiX * this.xIntegration.reduce((a, b) => a + b, 0);
    let costheta = Math.cos(theta);
    let sintheta = Math.sin(theta);

    let thetaDotDot =
      (-this.fx * costheta -
        this.mp * this.l * thetaDot ** 2 * costheta * sintheta -
        (this.mc + this.mp) * this.g * sintheta) /
      (this.l * (this.mc + this.mp * sintheta ** 2));

    let xDotDot =
      (this.fx +
        this.mp * sintheta * (this.l * thetaDot ** 2 + this.g * costheta)) /
      (this.mc + this.mp * sintheta ** 2);
    return [thetaDot, thetaDotDot, xDot, xDotDot];
  }

  noise() {
    return (Math.random() - 0.5) / 1000;
  }

  RK4(dt, theta, thetaDot, x, xDot) {
    let k1 = this.f(theta, thetaDot, x, xDot);
    let k2 = this.f(
      math.add(theta, 0.5 * dt * k1[0]),
      math.add(thetaDot, 0.5 * dt * k1[1]),
      math.add(x, 0.5 * dt * k1[2]),
      math.add(xDot, 0.5 * dt * k1[3])
    );
    let k3 = this.f(
      math.add(theta, 0.5 * dt * k2[0]),
      math.add(thetaDot, 0.5 * dt * k2[1]),
      math.add(x, 0.5 * dt * k2[2]),
      math.add(xDot, 0.5 * dt * k2[3])
    );
    let k4 = this.f(
      math.add(theta, 1.0 * dt * k3[0]),
      math.add(thetaDot, 1.0 * dt * k3[1]),
      math.add(x, 1.0 * dt * k3[2]),
      math.add(xDot, 1.0 * dt * k3[3])
    );

    let thetaDelta = (math.add(k1[0], 2 * k2[0], 2 * k3[0], k4[0]) * dt) / 6;
    let thetaDotDelta =
      (math.add(k1[1], 2 * k2[1], 2 * k3[1], 2 * k4[1]) * dt) / 6;
    let xDelta = (math.add(k1[2], 2 * k2[2], 2 * k3[2], k4[2]) * dt) / 6;
    let xDotDelta = (math.add(k1[3], 2 * k2[3], 2 * k3[3], 2 * k4[3]) * dt) / 6;

    return [
      math.add(theta, thetaDelta) + this.noise(),
      math.add(thetaDot, thetaDotDelta) + this.noise(),
      math.add(x, xDelta) + this.noise() + this.xOffset * dt,
      math.add(xDot, xDotDelta) + this.noise(),
    ];
  }

  tick(dt) {
    let newState;
    newState = this.RK4(dt, this.theta, this.thetaDot, this.x, this.xDot);
    this.theta = newState[0];
    this.thetaDot = newState[1];
    this.x = newState[2];
    this.xDot = newState[3];
    if (this.xIntegration.length > 30) this.xIntegration.shift();
    this.xIntegration.push(this.x);
  }

  get coordinates() {
    let x = this.x * this.mtocenti;
    if (isNaN(x)) x = 0;
    let bx = x;
    let by = 0;
    let theta = this.theta;
    if (isNaN(theta)) theta = 0;
    bx += this.l * Math.sin(theta) * this.mtocenti;
    by += this.l * Math.cos(theta) * this.mtocenti;
    return { bx, by, x };
  }

  get getl() {
    return this.l;
  }
}

export default CartPole;
